import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  createAddCardSessionRequest,
  parseCollectionMethodPatchResponse,
  patchSubscriptionCollectionMethodRequest,
} from 'services/subscriptions';
import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import { toast as addAppToast } from 'utils/toastAbstraction';

import ChoosePaymentMethodModal, {
  type PaymentMethodChoice,
} from './ChoosePaymentMethodModal';
import {
  CHOOSE_PAYMENT_METHOD_PARAMS,
  isChoosePaymentMethodDeepLink,
} from './constants';

type ChoosePaymentMethodFromUrlProps = {
  ready: boolean;
};

const redirectToExternalUrl = (url: string): void => {
  if (typeof window === 'undefined') return;
  window.location.href = url;
};

const ChoosePaymentMethodFromUrl = ({
  ready,
}: ChoosePaymentMethodFromUrlProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const openedFromUrlRef = useRef(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<PaymentMethodChoice | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearDeepLinkParams = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    (
      Object.keys(CHOOSE_PAYMENT_METHOD_PARAMS) as Array<
        keyof typeof CHOOSE_PAYMENT_METHOD_PARAMS
      >
    ).forEach((key) => {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    });
    if (!changed) return;
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [location.pathname, navigate, searchParams]);

  const resetModal = useCallback(() => {
    setSelected(null);
    setSubmitting(false);
    setRedirecting(false);
    setError(null);
  }, []);

  const closeModal = useCallback(() => {
    if (submitting || redirecting) return;
    setModalOpen(false);
    resetModal();
  }, [redirecting, resetModal, submitting]);

  useEffect(() => {
    const handlePageRestore = () => {
      setSubmitting(false);
      setRedirecting(false);
    };

    window.addEventListener('pageshow', handlePageRestore);
    return () => {
      window.removeEventListener('pageshow', handlePageRestore);
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (!isChoosePaymentMethodDeepLink(searchParams)) {
      openedFromUrlRef.current = false;
      return;
    }
    if (openedFromUrlRef.current) return;
    openedFromUrlRef.current = true;
    resetModal();
    setModalOpen(true);
    clearDeepLinkParams();
  }, [clearDeepLinkParams, ready, resetModal, searchParams]);

  const handleConfirm = useCallback(async () => {
    if (!selected || submitting || redirecting) return;
    setError(null);
    setSubmitting(true);
    if (selected === 'card') setRedirecting(true);
    let willRedirect = false;
    try {
      const data = await patchSubscriptionCollectionMethodRequest({
        method: selected,
      });
      const { addCardRequired, redirectUrl } =
        parseCollectionMethodPatchResponse(data);

      if (selected === 'card') {
        const cardUpdateUrl =
          redirectUrl ||
          (addCardRequired ? (await createAddCardSessionRequest())?.url : null);

        if (cardUpdateUrl) {
          willRedirect = true;
          redirectToExternalUrl(cardUpdateUrl);
          return;
        }

        if (!addCardRequired) {
          setModalOpen(false);
          resetModal();
          addAppToast(
            { title: t('notifications.billing_mode_updated') },
            { appearance: 'success', autoDismiss: true },
          );
          dispatch(getCurrentUserSubscription.trigger());
          return;
        }

        setError(t('subscriptions.profile.payment.choosePaymentErrorNoUrl'));
        return;
      }

      setModalOpen(false);
      resetModal();
      addAppToast(
        { title: t('notifications.billing_mode_updated') },
        { appearance: 'success', autoDismiss: true },
      );
      dispatch(getCurrentUserSubscription.trigger());
    } catch {
      setError(t('subscriptions.profile.payment.switchBillingErrorGeneric'));
    } finally {
      if (!willRedirect) {
        setSubmitting(false);
        setRedirecting(false);
      }
    }
  }, [dispatch, redirecting, resetModal, selected, submitting, t]);

  return (
    <ChoosePaymentMethodModal
      isOpen={modalOpen}
      selected={selected}
      submitting={submitting}
      redirecting={redirecting}
      error={error}
      onClose={closeModal}
      onSelect={setSelected}
      onConfirm={handleConfirm}
    />
  );
};

export default ChoosePaymentMethodFromUrl;
